// components/SuggestionsSlidePanel.js
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { PlusIcon } from "@heroicons/react/24/outline";

export default function SuggestionsSlidePanel({
  isOpen,
  onClose,
  suggestions,
  onSelectRecipe,
  feedback,
  setFeedback,
  onRegenerate,
}) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-30 flex justify-center items-end md:items-center bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose} // click outside to close
        >
          <motion.div
            className="w-full md:max-w-2xl rounded-t-2xl md:rounded-b-2xl md:h-[90%] h-[90vh] bg-white overflow-y-auto relative flex flex-col"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()} // prevent click inside from closing
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (info.offset.y > 100) onClose(); // swipe down to close
            }}
            style={{
              paddingBottom: "calc(var(--safe-area-inset-bottom) )", // safe area   + 0.75rem
            }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 p-4 pb-0">
              {/* Grab handle on mobile */}
              <div className="flex justify-center mb-2 pb-4 md:hidden">
                <div className="w-10 h-1 rounded bg-gray-300"></div>
              </div>
              <div className="hidden md:flex justify-end px-4">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-black text-2xl"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <h2 className="font-semibold text-lg px-2">Suggested Recipes</h2>
            </div>

            {/* Content */}
            <div className="px-4 py-4 space-y-4 overflow-y-auto flex-1">
              {suggestions?.map((s, idx) => (
                <div
                  key={idx}
                  className="border p-3 rounded bg-white shadow-sm space-y-2"
                >
                  <h3 className="text-lg font-bold">{s.name}</h3>
                  <p className="text-sm text-gray-600 italic">{s.summary}</p>
                  <div className="text-sm text-gray-600">
                    Prep: {s.prepTime} | Cook: {s.cookTime} | Serves:{" "}
                    {s.servings}
                  </div>

                  <button
                    onClick={() => onSelectRecipe(s)}
                    className="w-full flex items-center justify-center px-3 py-1 rounded-3xl border border-sage text-sage text-sm font-bold hover:bg-sage hover:text-white"
                  >
                    <PlusIcon className="w-4 h-4" /> Add to Calendar
                  </button>
                </div>
              ))}

              {/* Feedback box */}
              <div className="bg-sage p-4 rounded-lg">
                <h3 className="text-white font-medium">Feeling something else?</h3>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Provide feedback to improve suggestions"
                  className="border p-1 w-full mt-2 text-sm rounded"
                />
                <button
                  onClick={onRegenerate}
                  className="w-full bg-white px-3 py-1 text-charcoal rounded text-sm mt-2 hover:bg-gray-100"
                >
                  Get new suggestions
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
